import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { markerIcon } from './markerIcon';
import type { GeoPointDto } from '@/types/models';

export interface RouteMapMarker {
  point: GeoPointDto;
  label: string;
  color?: string;
}

export interface RouteMapProps {
  polyline: GeoPointDto[];
  markers?: RouteMapMarker[];
  className?: string;
}

/**
 * The driver's route, drawn on OpenStreetMap tiles (free, no API key). Display only --
 * the actual matching happens on the backend against the same stored geometry this
 * renders, so this component never computes distance or direction itself.
 *
 * <p>Plain Leaflet rather than react-leaflet: one dependency instead of two, and no
 * React-19 peer-dependency question to track. The map is imperative by nature (it owns
 * its own pan/zoom state); fighting that through a React wrapper buys little here.
 */
export function RouteMap({ polyline, markers = [], className }: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || polyline.length < 2) return;

    const map = L.map(containerRef.current, { scrollWheelZoom: false });
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    const latLngs = polyline.map((p): L.LatLngTuple => [p.latitude, p.longitude]);
    const line = L.polyline(latLngs, { color: '#0f766e', weight: 4 }).addTo(map);

    markers.forEach(({ point, label, color }) => {
      L.marker([point.latitude, point.longitude], { icon: markerIcon, title: label })
        .addTo(map)
        .bindTooltip(label, { permanent: false, ...(color ? {} : {}) });
    });

    map.fitBounds(line.getBounds(), { padding: [24, 24] });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Re-running only when the route or markers genuinely change avoids Leaflet
    // rebuilding the tile layer on every unrelated re-render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(polyline), JSON.stringify(markers)]);

  if (polyline.length < 2) {
    return null;
  }

  return <div ref={containerRef} className={className ?? 'h-64 w-full rounded-xl'} role="img" aria-label="Route map" />;
}
